import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { trackDashboardCtaClicked } from "analytics";
import { PATHS } from "constants/pathConstants";
import CologuardQualificationIntroModal from "components/v3/sharedComponents/modals/CologuardQualificationIntroModal";
import NextMoveCard from ".";
import Translate from "translate/Translate";
import { SHOW_ALL_NEXT_MOVE_CARDS } from "./showAllNextMoveCards";
import { useNextMoveMemberContext } from "./useNextMoveMemberContext";

const COLON_CANCER_BODY_BY_AGE_BAND = {
  "45-54":
    "Colon cancer is becoming more common in younger people. That's one of the reasons screening now begins at 45. The good news is that screening saves lives. Plus, there are now non-invasive options.",
  "55-plus":
    "Colon cancer is becoming more common in younger people. The good news is that screening saves lives. Plus, there are now non-invasive options to a colonoscopy."
} as const;

const COLON_CANCER_CTA_BY_AGE_BAND = {
  "45-54":
    "Take this brief questionnaire to see if you're up-to-date. If not, we can help you get the right screening for you.",
  "55-plus":
    "If you're 45 to 75 years old, take this brief questionnaire to see if you're up-to-date. If not, we can help you get the right screening for you."
} as const;

const CologuardQualificationNextMoveCard = () => {
  const history = useHistory();
  const [isIntroOpen, setIsIntroOpen] = useState(false);
  const {
    colonCancerScreeningAgeBand,
    hasCompletedCologuardQualificationSurvey,
    isEligibleForCologuardTestRedirect,
    isMemberEligibleForColonCancerScreeningPrompt,
    memberNeedsToTalkToNav
  } = useNextMoveMemberContext();

  const handleOpenIntro = useCallback(() => {
    setIsIntroOpen(true);
  }, []);

  const handleCloseIntro = useCallback(() => {
    setIsIntroOpen(false);
  }, []);

  const handleTakeQuestionnaire = useCallback(() => {
    trackDashboardCtaClicked({
      workflow: "colon_cancer_screening",
      ctaLocation: "next_move_card",
      ctaId: "colon_cancer_screening"
    });
    setIsIntroOpen(false);
    history.push(PATHS.COLOGUARD_QUALIFICATION_SURVEY);
  }, [history]);

  const ageBand =
    colonCancerScreeningAgeBand ??
    (SHOW_ALL_NEXT_MOVE_CARDS ? "45-54" : null);

  if (
    !SHOW_ALL_NEXT_MOVE_CARDS &&
    (!isEligibleForCologuardTestRedirect ||
      !isMemberEligibleForColonCancerScreeningPrompt ||
      !ageBand ||
      hasCompletedCologuardQualificationSurvey ||
      memberNeedsToTalkToNav)
  ) {
    return null;
  }

  if (!ageBand) {
    return null;
  }

  return (
    <>
      <NextMoveCard
        id="cologuard-qualification-next-move"
        tag="new"
        title={
          <Translate text="Are you up-to-date with your colon cancer screening?" />
        }
        body={
          <Translate
            text={COLON_CANCER_BODY_BY_AGE_BAND[ageBand]}
          />
        }
        subBody={
          <Translate
            text={COLON_CANCER_CTA_BY_AGE_BAND[ageBand]}
          />
        }
        link={{
          onClick: handleOpenIntro,
          overrideClick: true,
          url: PATHS.COLOGUARD_QUALIFICATION_SURVEY
        }}
      />
      <CologuardQualificationIntroModal
        isOpen={isIntroOpen}
        onClose={handleCloseIntro}
        onTakeQuestionnaire={handleTakeQuestionnaire}
      />
    </>
  );
};

export default CologuardQualificationNextMoveCard;
